import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApiClient } from '../api/client';
import { MetricCard, PageHeader, PageShell } from '../components/ui';
import { useI18n } from '../i18n/I18nProvider';

type Daily = { date: string; co2SavedKg: number; rides: number; moneySaved: number };
type DashboardDto = {
  communityId: string;
  range: string;
  daily: Daily[];
  totalCo2SavedKg: number;
  totalRides: number;
  totalMoneySaved: number;
  tripsCompleted: number;
  averageTripDistanceKm: number;
  averageOccupancyRate: number;
  ridesPerUser: number;
};

const ActivityChart: React.FC<{ labels: string[]; rides: number[]; co2: number[]; label: string }> = ({ labels, rides, co2, label }) => {
  const width = Math.max(420, labels.length * 56);
  const height = 260;
  const pad = { top: 20, right: 18, bottom: 34, left: 18 };
  const plotWidth = width - pad.left - pad.right;
  const plotHeight = height - pad.top - pad.bottom;
  const step = plotWidth / Math.max(labels.length, 1);
  const barWidth = Math.min(28, step * 0.48);
  const rideMax = Math.max(...rides, 1);
  const co2Max = Math.max(...co2, 1);
  const x = (index: number) => pad.left + step * (index + 0.5);
  const yRide = (value: number) => pad.top + plotHeight - (value / rideMax) * plotHeight;
  const yCo2 = (value: number) => pad.top + plotHeight - (value / co2Max) * plotHeight;
  const line = co2.map((value, index) => `${index ? 'L' : 'M'} ${x(index)} ${yCo2(value)}`).join(' ');

  return (
    <div className="admin-chart-scroll">
      <svg className="admin-chart-svg" width={width} height={height} role="img" aria-label={label}>
        {[0, 1, 2, 3].map((lineIndex) => {
          const y = pad.top + plotHeight * lineIndex / 3;
          return <line key={lineIndex} x1={pad.left} x2={width - pad.right} y1={y} y2={y} stroke="#e8edf0" strokeWidth="1" />;
        })}
        {rides.map((value, index) => {
          const y = yRide(value);
          return <rect key={index} x={x(index) - barWidth / 2} y={y} width={barWidth} height={Math.max(2, pad.top + plotHeight - y)} rx="5" fill="#d8c8f4" />;
        })}
        <path d={line} fill="none" stroke="#6b2bd9" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {co2.map((value, index) => <circle key={index} cx={x(index)} cy={yCo2(value)} r="3" fill="#6b2bd9" />)}
        {labels.map((labelText, index) => <text key={index} x={x(index)} y={height - 7} textAnchor="middle" fontSize="11" fill="#617481">{labelText}</text>)}
      </svg>
    </div>
  );
};

const SavingsChart: React.FC<{ labels: string[]; values: number[]; label: string }> = ({ labels, values, label }) => {
  const width = Math.max(360, labels.length * 48);
  const height = 170;
  const pad = { top: 12, right: 12, bottom: 30, left: 12 };
  const plotHeight = height - pad.top - pad.bottom;
  const step = (width - pad.left - pad.right) / Math.max(values.length, 1);
  const max = Math.max(...values, 1);
  return (
    <div className="admin-chart-scroll">
      <svg className="admin-chart-svg" width={width} height={height} role="img" aria-label={label}>
        {values.map((value, index) => {
          const barHeight = Math.max(2, value / max * plotHeight);
          return <g key={index}>
            <rect x={pad.left + step * index + step * 0.25} y={pad.top + plotHeight - barHeight} width={step * 0.5} height={barHeight} rx="4" fill="#a9d9c4" />
            <text x={pad.left + step * (index + 0.5)} y={height - 5} textAnchor="middle" fontSize="10" fill="#617481">{labels[index]}</text>
          </g>;
        })}
      </svg>
    </div>
  );
};

export const CommunityDashboardPage: React.FC = () => {
  const { id } = useParams();
  const { request } = useApiClient();
  const navigate = useNavigate();
  const { t, language } = useI18n();
  const [range, setRange] = useState<'week' | 'month'>('month');
  const [data, setData] = useState<DashboardDto | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(false);
    request<DashboardDto>(`/communities/${id}/dashboard?range=${range}`)
      .then(setData)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [id, range, request, reload]);

  const formatter = useMemo(() => new Intl.DateTimeFormat(language === 'de' ? 'de-AT' : 'en-GB', range === 'month' ? { day: 'numeric', month: 'short' } : { weekday: 'short' }), [language, range]);
  const labels = data?.daily.map((day) => formatter.format(new Date(day.date.length === 10 ? `${day.date}T00:00:00` : day.date))) ?? [];
  const rides = data?.daily.map((day) => day.rides) ?? [];
  const co2 = data?.daily.map((day) => day.co2SavedKg) ?? [];
  const savings = data?.daily.map((day) => day.moneySaved) ?? [];
  const number = useMemo(() => new Intl.NumberFormat(language === 'de' ? 'de-AT' : 'en-GB', { maximumFractionDigits: 1 }), [language]);

  return (
    <PageShell>
      <PageHeader
        title={t('communities.dashboard')}
        description={t('communities.activity')}
        actions={(
          <>
            <div className="admin-segmented-control" aria-label={t('communities.activity')}>
              <button type="button" aria-pressed={range === 'week'} onClick={() => setRange('week')}>{t('communities.period.week')}</button>
              <button type="button" aria-pressed={range === 'month'} onClick={() => setRange('month')}>{t('communities.period.month')}</button>
            </div>
            <button className="btn btn-neutral" onClick={() => navigate(`/communities/${id}`)}>{t('communities.back')}</button>
          </>
        )}
      />

      {error ? (
        <div className="admin-panel flex flex-col items-start gap-3 p-5" role="alert">
          <p className="text-sm text-red-700">{t('communities.loadDashboardError')}</p>
          <button className="btn btn-neutral" onClick={() => setReload((value) => value + 1)}>{t('communities.retry')}</button>
        </div>
      ) : loading || !data ? (
        <div className="admin-panel p-5 text-sm text-slate-500" role="status">{t('common.loading')}</div>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard label={t('communities.completed')} value={data.tripsCompleted} tone="violet" />
            <MetricCard label={t('communities.totalCo2')} value={`${number.format(data.totalCo2SavedKg)} kg`} tone="green" />
            <MetricCard label={t('communities.occupancy')} value={`${number.format(data.averageOccupancyRate)}×`} tone="blue" />
            <MetricCard label={t('communities.ridesPerUser')} value={number.format(data.ridesPerUser)} tone="slate" />
          </div>

          <section className="admin-panel mt-4 p-5">
            <div className="admin-section-heading">
              <div><h2>{t('communities.chartActivity')}</h2><p>{t('communities.chartLegend')}</p></div>
              <span className="admin-chart-total">{t('communities.totalRides')}: <strong>{data.totalRides}</strong></span>
            </div>
            <ActivityChart labels={labels} rides={rides} co2={co2} label={t('communities.chartActivity')} />
          </section>

          <div className="mt-4">
            <section className="admin-panel p-5">
              <div className="admin-section-heading"><div><h2>{t('communities.moneyPerDay')}</h2><p>{t('communities.totalMoney')}: € {number.format(data.totalMoneySaved)}</p></div></div>
              <SavingsChart labels={labels} values={savings} label={t('communities.moneyPerDay')} />
            </section>
          </div>
        </>
      )}
    </PageShell>
  );
};
